import { INTERNAL_ONLY_ROUTE_KEYS, routes } from "~/app/routes/web-routes.ts";

/**
 * Helper to extract the base path from a route pattern.
 * E.g., "/quote-details/:quoteId" -> "/quote-details"
 */
function getBasePath(pattern: string): string {
	const paramIndex = pattern.indexOf(":");
	return paramIndex === -1
		? pattern
		: pattern.slice(0, Math.max(0, paramIndex - 1));
}

function isInternalOnlyRoute(routeKey: string): boolean {
	return INTERNAL_ONLY_ROUTE_KEYS.includes(
		routeKey as (typeof INTERNAL_ONLY_ROUTE_KEYS)[number],
	);
}

const DEFAULT_ROUTE_OPTIONS = { includeInternal: false };

/**
 * Checks if a pathname matches any known external TRAX route.
 * Derives valid routes from the routes object, excluding internal-only routes.
 * Supports both exact paths and prefix-based parameterized routes.
 *
 * @param pathname The URL pathname to validate (without protocol/host/search/hash)
 * @returns true if pathname matches a valid external route, false otherwise
 */
export function isValidExternalRoute(pathname: string): boolean {
	return isValidRoute(pathname, DEFAULT_ROUTE_OPTIONS);
}

/**
 * Checks if a pathname matches any known TRAX route.
 * Supports both exact paths and prefix-based parameterized routes.
 *
 * @param pathname The URL pathname to validate (without protocol/host/search/hash)
 * @param options Configuration for route validation
 * @param options.includeInternal Whether to include internal-only routes (default: false)
 * @returns true if pathname matches a valid route, false otherwise
 */
export function isValidRoute(
	pathname: string,
	options?: { readonly includeInternal?: boolean },
): boolean {
	const { includeInternal = false } = options ?? {};
	for (const [key, pattern] of Object.entries(routes)) {
		// Skip internal-only routes if not including them
		if (!includeInternal && isInternalOnlyRoute(key)) {
			continue;
		}

		// Check for exact match
		if (pathname === pattern) {
			return true;
		}

		// Check for prefix match (parameterized routes)
		if (pattern.includes(":")) {
			const basePath = getBasePath(pattern);
			if (pathname === basePath || pathname.startsWith(basePath + "/")) {
				return true;
			}
		}
	}

	return false;
}
